import type { CarouselProps } from "components/HomeCarousel/HomeCarousel";
import { DeprecatedBandCButton } from "packages/basket-and-checkout/shared/components/Button/Button";
import { CarouselModule } from "packages/ui-shared/components/CarouselModule";
import React from "react";
import type {
  RecommendationSection,
  Tracking,
  Maybe,
  ProductCardProduct,
  VariantType,
  VariantTypeV2
} from "@shared/graphql.types";
import { BasketSectionsHeader } from "packages/basket-and-checkout/shared/components/BasketSectionsHeader/BasketSectionsHeader";
import { inertSectionWrapper } from "packages/basket-and-checkout/shared/components/BasketSectionsHeader/BasketSectionsHeader.css";
import {
  footerCTA,
  productCardWrapperContainer,
  recommendationContainer
} from "./RecommendationModule.css";
import { toHtmlId } from "./utils/toHtmlId";
import { trackClicked } from "tracking/utils/trackClicked";
import { ProductCardWrapper } from "packages/acquisition/shared/components/ProductCardWrapper/ProductCardWrapper";
import { variantToProductCard } from "packages/core-shopping/shared/variantToProductCard";
import { useLocale } from "hooks";
import { localeToRegion } from "utils/i18n";

const RecommendationCarousel = ({
  tracking,
  carouselPropsOverride = {},
  carouselWrapperClassName = "",
  onAddToBag,
  locationType,
  variants
}: {
  tracking: Maybe<Tracking> | undefined;
  carouselPropsOverride?: CarouselProps;
  carouselWrapperClassName?: string;
  onAddToBag?: (product: ProductCardProduct | VariantType) => void;
  locationType: string;
  variants?: VariantTypeV2[];
}) => {
  const locale = useLocale();
  if (!variants || variants.length === 0) {
    return <></>;
  }

  return (
    <CarouselModule
      carouselWrapperClassName={`${productCardWrapperContainer} ${carouselWrapperClassName}`}
      tracking={tracking}
      carouselProps={carouselPropsOverride}
    >
      {variants.map((variant) => {
        const productCardData = variantToProductCard(variant, {
          store: localeToRegion[locale] as "uk" | "us"
        });
        return (
          <ProductCardWrapper
            key={variant.id}
            productCard={productCardData}
            data-product-result="constructor"
            location={locationType}
            addToBasketOptions={{
              afterAdd: (productVariant) => onAddToBag?.(productVariant)
            }}
            styles={{ variant: "carousel", buttonVariant: "secondary" }}
          />
        );
      })}
    </CarouselModule>
  );
};

const componentTypeMap = {
  CAROUSEL: RecommendationCarousel
};

export const RecommendationModule = ({
  id,
  componentType,
  title,
  description,
  tracking,
  carouselPropsOverride = {},
  carouselWrapperClassName = "",
  locationType,
  onAddToBag,
  cta,
  onCtaClick,
  variants
}: Omit<RecommendationSection, "items"> & {
  carouselWrapperClassName?: string;
  carouselPropsOverride?: CarouselProps;
  onAddToBag?: (product: ProductCardProduct | VariantType) => void;
  onCtaClick?: () => void;
  variants?: VariantTypeV2[];
}) => {
  const Component = componentTypeMap[componentType];

  if (!Component) return null;

  if (!variants || variants.length === 0) return null;

  return (
    <div
      id={toHtmlId(id)}
      data-testid="recommendation-section"
      className={recommendationContainer}
    >
      <BasketSectionsHeader
        title={title || ""}
        description={description || ""}
        {...(cta?.type === "header" ? { cta } : {})}
        {...(onCtaClick ? { onCtaClick } : {})}
      />
      <div className={inertSectionWrapper}>
        <Component
          tracking={tracking}
          onAddToBag={onAddToBag}
          locationType={locationType}
          variants={variants}
          {...(componentType === "CAROUSEL"
            ? {
                carouselPropsOverride: carouselPropsOverride,
                carouselWrapperClassName
              }
            : {})}
        />
      </div>
      {cta?.type === "footer" && (
        <DeprecatedBandCButton
          className={footerCTA}
          as="a"
          variant="secondary"
          href={cta.link as string}
          onClick={() => {
            if (cta.tracking?.clicked) {
              trackClicked(cta.tracking.clicked);
            }
          }}
        >
          {cta.text}
        </DeprecatedBandCButton>
      )}
    </div>
  );
};
